import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Search, X, Pencil, Trash2 } from "lucide-react";
import api from "@/utils/connector/ApiConnector";
import { useUser } from "@/utils/userContext/UserContext";
import { Affiliate } from "@/utils/models/Affiliate";
import { useGlobalModal } from "@/components/GlobalModalProvider/GlobalModalProvider";
import { useInvalidateCache } from "@/utils/hooks/useInvalidateCache";
import { showApiErrorModal } from "@/utils/common/apiErrorHelper";
import "./Afiliados.css";

const AFFILIATES_PATH = "/affiliate";

interface AffiliateFilters {
  name: string;
  email: string;
}

async function fetchAffiliatesRequest(
  token: string | undefined,
  page = 1,
  pageSize = 20,
  filters?: AffiliateFilters
) {
  return api.get(AFFILIATES_PATH, {
    params: {
      page,
      pageSize,
      name: filters?.name || undefined,
      email: filters?.email || undefined,
    },
    headers: { Authorization: `Bearer ${token}` },
  });
}

async function createAffiliateRequest(token: string | undefined, payload: Partial<Affiliate>) {
  return api.post(AFFILIATES_PATH, payload, {
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
  });
}

async function updateAffiliateRequest(token: string | undefined, id: string, payload: Partial<Affiliate>) {
  return api.patch(`${AFFILIATES_PATH}/${id}`, payload, {
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
  });
}

async function deleteAffiliateRequest(token: string | undefined, id: string) {
  return api.delete(`${AFFILIATES_PATH}/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
}

/* ---------- ConfirmModal ---------- */
function ConfirmModal({
  open,
  title = "Confirmação",
  message,
  confirmLabel = "Remover",
  cancelLabel = "Cancelar",
  loading = false,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title?: string;
  message?: string | React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  if (!open) return null;

  return (
    <div className="pp-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="confirm-title">
      <div className="pp-modal pp-modal-confirm" role="document">
        <h3 id="confirm-title" className="pp-modal-title">{title}</h3>
        <div className="pp-modal-body">
          {message}
        </div>
        <div className="pp-modal-actions">
          <button className="pp-btn pp-btn-ghost" onClick={onCancel} disabled={loading}>{cancelLabel}</button>
          <button className="pp-btn pp-btn-danger" onClick={onConfirm} disabled={loading}>
            {loading ? 'Removendo...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Modal ---------- */
function Modal({ open, onClose, children }: { open: boolean; onClose: () => void; children?: React.ReactNode }) {
  if (!open) return null;
  return (
    <div className="pp-modal-overlay" role="dialog" aria-modal="true">
      <div className="pp-modal">
        <button className="pp-modal-close" onClick={onClose} aria-label="Fechar">✕</button>
        {children}
      </div>
    </div>
  );
}

/* ---------- AffiliateForm ---------- */
function AffiliateForm({
  initial,
  onCancel,
  onSave,
  submitting,
}: {
  initial?: Partial<Affiliate>;
  onCancel: () => void;
  onSave: (_data: {
    name: string;
    email: string;
    phone: string;
    companyDocument: string;
    postalBoxNumber: string;
    legalRepresentativeName: string;
    legalRepresentativeDocument: string;
    birthDate: string;
    address: string;
  }) => void;
  submitting?: boolean;
}) {
  const [name, setName] = useState(initial?.name ?? "");
  const [email, setEmail] = useState(initial?.email ?? "");
  const [phone, setPhone] = useState(initial?.phone ?? "");
  const [companyDocument, setCompanyDocument] = useState(initial?.companyDocument ?? "");
  const [postalBoxNumber, setPostalBoxNumber] = useState(initial?.postalBoxNumber ?? "");
  const [legalRepresentativeName, setLegalRepresentativeName] = useState(initial?.legalRepresentativeName ?? "");
  const [legalRepresentativeDocument, setLegalRepresentativeDocument] = useState(initial?.legalRepresentativeDocument ?? "");
  const [birthDate, setBirthDate] = useState(initial?.birthDate ? initial.birthDate.substring(0, 10) : "");
  const [address, setAddress] = useState(initial?.address ?? "");

  const modal = useGlobalModal();

  useEffect(() => {
    setName(initial?.name ?? "");
    setEmail(initial?.email ?? "");
    setPhone(initial?.phone ?? "");
    setCompanyDocument(initial?.companyDocument ?? "");
    setPostalBoxNumber(initial?.postalBoxNumber ?? "");
    setLegalRepresentativeName(initial?.legalRepresentativeName ?? "");
    setLegalRepresentativeDocument(initial?.legalRepresentativeDocument ?? "");
    setBirthDate(initial?.birthDate ? initial.birthDate.substring(0, 10) : "");
    setAddress(initial?.address ?? "");
  }, [initial]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!name?.trim()) {
      modal?.show({ type: "warning", title: "Campos vazios", message: "Preencha o nome do afiliado!", autoClose: 3000 });
      return;
    }

    if (!email?.trim()) {
      modal?.show({ type: "warning", title: "Campos vazios", message: "Preencha o e-mail do afiliado!", autoClose: 3000 });
      return;
    }

    if (!phone?.trim()) {
      modal?.show({ type: "warning", title: "Campos vazios", message: "Preencha o telefone do afiliado!", autoClose: 3000 });
      return;
    }

    onSave({
      name: name.trim(),
      email: email.trim(),
      phone: phone.trim(),
      companyDocument: companyDocument.trim(),
      postalBoxNumber: postalBoxNumber.trim(),
      legalRepresentativeName: legalRepresentativeName.trim(),
      legalRepresentativeDocument: legalRepresentativeDocument.trim(),
      birthDate: birthDate,
      address: address.trim(),
    });
  };

  return (
    <form className="pp-form" onSubmit={handleSubmit}>
      <label className="pp-field">
        <span className="pp-label">Nome *</span>
        <input className="pp-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nome do afiliado" />
      </label>

      <label className="pp-field">
        <span className="pp-label">E-mail *</span>
        <input className="pp-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@exemplo.com" />
      </label>

      <label className="pp-field">
        <span className="pp-label">Telefone *</span>
        <input className="pp-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+5511999999999" />
      </label>

      <label className="pp-field">
        <span className="pp-label">Documento da Empresa</span>
        <input className="pp-input" value={companyDocument} onChange={(e) => setCompanyDocument(e.target.value)} placeholder="CNPJ da empresa" />
      </label>

      <label className="pp-field">
        <span className="pp-label">Caixa Postal</span>
        <input className="pp-input" value={postalBoxNumber} onChange={(e) => setPostalBoxNumber(e.target.value)} placeholder="Número da caixa postal" />
      </label>

      <label className="pp-field">
        <span className="pp-label">Representante Legal</span>
        <input className="pp-input" value={legalRepresentativeName} onChange={(e) => setLegalRepresentativeName(e.target.value)} placeholder="Nome do representante legal" />
      </label>

      <label className="pp-field">
        <span className="pp-label">Documento do Representante</span>
        <input className="pp-input" value={legalRepresentativeDocument} onChange={(e) => setLegalRepresentativeDocument(e.target.value)} placeholder="CPF/CNPJ do representante" />
      </label>

      <label className="pp-field">
        <span className="pp-label">Data de Nascimento</span>
        <input className="pp-input" type="date" value={birthDate} onChange={(e) => setBirthDate(e.target.value)} />
      </label>

      <label className="pp-field">
        <span className="pp-label">Endereço</span>
        <input className="pp-input" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Endereço completo" />
      </label>

      <div className="pp-actions">
        <button type="button" className="pp-btn pp-btn-ghost" onClick={onCancel}>Cancelar</button>
        <button type="submit" className="pp-btn pp-btn-primary" disabled={submitting}>{submitting ? 'Salvando...' : 'Salvar'}</button>
      </div>
    </form>
  );
}

/* ---------- Fetch function ---------- */
async function fetchAffiliates(token: string, page: number, pageSize: number, filters?: AffiliateFilters) {
  const resp = await fetchAffiliatesRequest(token, page, pageSize, filters);
  const raw = (resp && (resp as any).data) ? (resp as any).data : resp;
  let list: Affiliate[] = [];
  if (Array.isArray(raw)) list = raw as Affiliate[];
  else if (Array.isArray(raw.items)) list = raw.items as Affiliate[];
  else if (Array.isArray(raw.data)) list = raw.data as Affiliate[];
  else if (Array.isArray((raw as any).affiliates)) list = (raw as any).affiliates as Affiliate[];
  const tot = raw?.total ?? raw?.count ?? raw?.totalItems ?? null;
  return { affiliates: list, total: typeof tot === 'number' ? tot : null };
}

/* ---------- Main Component ---------- */
export default function Afiliados() {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<Affiliate | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [saving, setSaving] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deletingName, setDeletingName] = useState<string | null>(null);

  const [searchName, setSearchName] = useState('');
  const [debouncedName, setDebouncedName] = useState('');
  const [searchEmail, setSearchEmail] = useState('');
  const [debouncedEmail, setDebouncedEmail] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const userContext = useUser();
  const token = userContext?.userData?.auth;

  const modal = useGlobalModal();
  const { invalidateAffiliates } = useInvalidateCache();
  const queryClient = useQueryClient();

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedName(searchName), 300);
    return () => window.clearTimeout(timer);
  }, [searchName]);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedEmail(searchEmail), 300);
    return () => window.clearTimeout(timer);
  }, [searchEmail]);

  const filters: AffiliateFilters = {
    name: debouncedName,
    email: debouncedEmail,
  };

  const { data, isLoading: loading } = useQuery({
    queryKey: ['affiliates', page, pageSize, debouncedName, debouncedEmail],
    queryFn: () => fetchAffiliates(token!, page, pageSize, filters),
    enabled: !!token,
  });

  const affiliates = data?.affiliates ?? [];
  const total = data?.total ?? null;

  const clearFilters = () => {
    setSearchName('');
    setSearchEmail('');
    setPage(1);
  };

  const hasActiveFilters = searchName || searchEmail;

  const handleCreate = async (payload: {
    name: string;
    email: string;
    phone: string;
    companyDocument: string;
    postalBoxNumber: string;
    legalRepresentativeName: string;
    legalRepresentativeDocument: string;
    birthDate: string;
    address: string;
  }) => {
    setSaving(true);
    try {
      await createAffiliateRequest(token, payload);
      setCreateOpen(false);
      setPage(1);
      modal?.show({ type: "success", title: "Sucesso", message: "Afiliado criado.", autoClose: 2500 });
      await invalidateAffiliates();
      await queryClient.invalidateQueries({ queryKey: ['affiliates'] });
    } catch (err: any) {
      console.error('Erro ao criar afiliado:', err);
      showApiErrorModal(modal, err, {
        title: "Erro ao criar afiliado",
        fallbackMessage: "Erro ao criar afiliado",
        autoClose: 3000,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (id: string, payload: {
    name: string;
    email: string;
    phone: string;
    companyDocument: string;
    postalBoxNumber: string;
    legalRepresentativeName: string;
    legalRepresentativeDocument: string;
    birthDate: string;
    address: string;
  }) => {
    setSaving(true);
    try {
      await updateAffiliateRequest(token, id, payload);
      modal?.show({ type: "success", title: "Sucesso", message: "Afiliado atualizado com sucesso!", autoClose: 3000 });
      setEditOpen(false);
      setEditing(null);
      await invalidateAffiliates();
      await queryClient.invalidateQueries({ queryKey: ['affiliates'] });
    } catch (err) {
      console.error('Erro ao atualizar afiliado:', err);
      showApiErrorModal(modal, err, {
        title: "Erro ao atualizar afiliado",
        fallbackMessage: "Erro ao atualizar afiliado",
        autoClose: 3000,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancelDelete = () => {
    setConfirmOpen(false);
    setDeletingId(null);
    setDeletingName(null);
  };

  const handleConfirmDelete = async () => {
    if (!deletingId) return;
    setDeleting(true);
    try {
      await deleteAffiliateRequest(token, deletingId);
      setConfirmOpen(false);
      setDeletingId(null);
      setDeletingName(null);
      modal?.show({ type: "success", title: "Sucesso", message: "Afiliado removido com sucesso!", autoClose: 3000 });
      await invalidateAffiliates();
      await queryClient.invalidateQueries({ queryKey: ['affiliates'] });
    } catch (err) {
      console.error('Erro ao remover afiliado:', err);
      showApiErrorModal(modal, err, {
        title: "Erro ao remover afiliado",
        fallbackMessage: "Erro ao remover afiliado",
        autoClose: 3000,
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="pp-page">
      <header className="pp-header">
        <div>
          <h1 className="pp-title">Afiliados</h1>
          <p className="pp-sub dashboard-page-subtitle">Gerencie os afiliados e parceiros do coworking</p>
        </div>
        <div>
          <button className="pp-btn pp-btn-primary" onClick={() => setCreateOpen(true)}>+ Novo Afiliado</button>
        </div>
      </header>

      {/* Search and Filter Section */}
      <div className="pp-filters-section">
        <div className="pp-search-container">
          <div className="pp-search-input-wrapper">
            <Search className="pp-search-icon" size={16} />
            <input
              type="text"
              placeholder="Buscar por nome..."
              className="pp-search-input"
              value={searchName}
              onChange={(e) => { setSearchName(e.target.value); setPage(1); }}
            />
          </div>
          <button
            className={`pp-filter-toggle ${showFilters ? 'active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            Filtros {hasActiveFilters && <span className="pp-filter-badge">●</span>}
          </button>
          {hasActiveFilters && (
            <button className="pp-clear-filters" onClick={clearFilters}>
              <X size={14} /> Limpar
            </button>
          )}
        </div>

        {showFilters && (
          <div className="pp-filters-row">
            <div className="pp-filter-group">
              <label className="pp-filter-label">E-mail</label>
              <input
                type="text"
                className="pp-filter-input"
                placeholder="Filtrar por e-mail..."
                value={searchEmail}
                onChange={(e) => { setSearchEmail(e.target.value); setPage(1); }}
              />
            </div>
          </div>
        )}
      </div>

      <section className="pp-card">
        <div className="pp-table-wrapper">
          <table className="pp-table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>E-mail</th>
                <th>Telefone</th>
                <th>Documento</th>
                <th>Representante</th>
                <th>Endereço</th>
                <th style={{ textAlign: 'center' }}></th>
              </tr>
            </thead>
            <tbody className="pp-table-body">
              {loading ? (
                <tr><td colSpan={7} className="pp-empty">Carregando...</td></tr>
              ) : affiliates.length === 0 ? (
                <tr><td colSpan={7} className="pp-empty">Nenhum afiliado encontrado.</td></tr>
              ) : (
                affiliates.map((a) => (
                  <tr key={a.id}>
                    <td className="pp-td-name">{a.name || '—'}</td>
                    <td className="pp-td-center">{a.email}</td>
                    <td className="pp-td-center">{a.phone}</td>
                    <td className="pp-td-center">{a.companyDocument || '—'}</td>
                    <td className="pp-td-center">{a.legalRepresentativeName || '—'}</td>
                    <td className="pp-td-center">{a.address || '—'}</td>
                    <td className="pp-td-actions">
                      <button className="pp-action pp-action-edit" title="Editar afiliado" onClick={() => { setEditing(a); setEditOpen(true); }}>
                        <Pencil size={14} /> Editar
                      </button>
                      <button
                        className="pp-action pp-action-delete"
                        title="Excluir afiliado"
                        onClick={() => { setDeletingId(a.id); setDeletingName(a.name || 'Afiliado'); setConfirmOpen(true); }}
                      >
                        <Trash2 size={14} /> Apagar
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="pp-pagination">
          <div className="pp-total">{total ? `Total: ${total}` : ''}</div>
          <div className="pp-pages">
            <button className="pp-page-btn" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>‹</button>
            <div className="pp-page-indicator">{page}</div>
            <button className="pp-page-btn" onClick={() => setPage((p) => p + 1)}>›</button>
          </div>
        </div>
      </section>

      <Modal open={createOpen} onClose={() => setCreateOpen(false)}>
        <h2 className="pp-modal-title">Novo Afiliado</h2>
        <AffiliateForm onCancel={() => setCreateOpen(false)} onSave={handleCreate} submitting={saving} />
      </Modal>

      <Modal open={editOpen} onClose={() => setEditOpen(false)}>
        <h2 className="pp-modal-title">Editar Afiliado</h2>
        <AffiliateForm initial={editing ?? undefined} onCancel={() => setEditOpen(false)} onSave={(payload) => editing && handleEdit(editing.id, payload)} submitting={saving} />
      </Modal>

      <ConfirmModal
        open={confirmOpen}
        title="Remover afiliado"
        message={deletingName ? `Deseja remover o afiliado "${deletingName}"? Esta ação não poderá ser desfeita.` : "Deseja remover este afiliado?"}
        confirmLabel="Remover"
        cancelLabel="Cancelar"
        loading={deleting}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
}
